import { z } from 'zod';
import {
  AlcoholHabit,
  ChildrenCustody,
  ChildrenLivingSituation,
  ConvertStatus,
  Diet,
  DressStyleMan,
  DressStyleWoman,
  EducationLevel,
  EmploymentStatus,
  FamilyEducationLevel,
  FamilyInvolvement,
  FamilyPractice,
  FamilyResidence,
  FastingFrequency,
  Gender,
  HajjStatus,
  IncomeBracket,
  Industry,
  LivingArrangement,
  MahrExpectation,
  MaritalStatus,
  MarriageTimeframe,
  MosqueAttendance,
  PolygamyPreference,
  PrayerFrequency,
  ProfileIntroModerationStatus,
  QuranEngagement,
  QuranMemorization,
  Relocation,
  Sect,
  SeekingPace,
  SmokingHabit,
  SubSect,
  UmrahStatus,
  WantsChildren,
} from '../enums.js';
import { MIN_AGE_YEARS, ageFromDob } from '../helpers/age.js';
import {
  GeoPointSchema,
  Iso3CountrySchema,
  IsoDateTimeSchema,
  IsoLanguageSchema,
  UuidSchema,
} from '../primitives.js';

// Custom schema (not just IsoDateSchema + refine) so the messages
// surfaced to the user are human-readable. IsoDateSchema's default
// regex message ("must be YYYY-MM-DD") is correct but technical —
// the wizard's date input gives users a calendar picker, so they
// don't think in ISO formats. "Please pick your date of birth." is
// the message they actually need to see for empty or partial input.
const DateOfBirthSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Please pick your date of birth.' })
  .refine((dob) => ageFromDob(dob) >= MIN_AGE_YEARS, {
    message: `You must be at least ${MIN_AGE_YEARS} years old to join funkihoo.`,
  });

/**
 * Full matrimonial profile. 1:1 with User. Built up via the wizard and
 * editable post-wizard via Settings → Edit profile.
 *
 * Dress-style is gender-dependent — clients render the correct enum based on
 * the profile's gender and validate the field accordingly at the API edge.
 */
export const ProfileSchema = z.object({
  userId: UuidSchema,

  // ─── Basics ─────────────────────────────────────────────────────────
  // Custom messages so the user sees plain language instead of zod's
  // "String must contain at least 2 character(s)" technical default.
  displayName: z
    .string()
    .trim()
    .min(2, { message: 'Please enter your name (at least 2 characters).' })
    .max(40, { message: 'Display name must be 40 characters or fewer.' }),
  dateOfBirth: DateOfBirthSchema,
  gender: Gender,
  heightCm: z.number().int().min(120).max(230),
  maritalStatus: MaritalStatus,
  hasChildren: z.boolean(),
  childrenCount: z.number().int().min(0).max(20).nullable(),
  childrenCustody: ChildrenCustody.nullable(),
  nationalityIso: Iso3CountrySchema,
  countryOfResidenceIso: Iso3CountrySchema,
  ethnicities: z.array(z.string().min(1).max(40)).min(1).max(5),
  languages: z.array(IsoLanguageSchema).min(1).max(8),
  educationLevel: EducationLevel,
  /** Field of study (e.g. Engineering, Medicine). Optional —
   *  some users prefer to share only the level. Free-form text up
   *  to 80 chars; UI presents a curated dropdown of common
   *  disciplines, validated by the registry option list. */
  educationDiscipline: z.string().trim().max(80).nullable(),
  profession: z.string().trim().min(1).max(80),
  /**
   * Industry — coarser than `profession`. Optional disclosure;
   * default `prefer_not_to_say`. Matchable signal for South Asian /
   * MENA markets where industry strata predict compatibility.
   * Matrimonial-polish audit PR #4.
   */
  industry: Industry.default('prefer_not_to_say'),
  /**
   * Employment status — captures "still studying" / "homemaker" /
   * "between roles" with more dignity than burying it inside
   * `profession`. Optional; default `prefer_not_to_say`.
   */
  employmentStatus: EmploymentStatus.default('prefer_not_to_say'),
  /**
   * Mother tongue — the language the user is most comfortable in,
   * especially for nikah / family conversations. Distinct from
   * `languages` (multi-select of all spoken). Optional + nullable.
   * Used by the matcher as a re-rank signal: shared mother tongue
   * predicts a lower-friction family conversation.
   */
  motherTongue: IsoLanguageSchema.nullable().default(null),
  grewUpCountryIso: Iso3CountrySchema.nullable(),
  grewUpRegion: z.string().trim().max(80).nullable(),

  // ─── Religion ───────────────────────────────────────────────────────
  sect: Sect,
  /** Closed enum — see `SubSect` in enums.ts. `na` is the explicit
   *  "doesn't apply" answer; `other` covers schools we don't enumerate.
   *  Informational at MVP; `Sect` remains the hard-filter axis. */
  subSect: SubSect,
  religiosity: z.number().int().min(1).max(5),
  prays: PrayerFrequency,
  fastsRamadan: FastingFrequency,
  quranEngagement: QuranEngagement,
  dressStyle: z.union([DressStyleWoman, DressStyleMan]),
  diet: Diet,
  alcohol: AlcoholHabit,
  smoking: SmokingHabit,
  convertStatus: ConvertStatus,

  // ─── Islamic-practice depth (matrimonial-polish PR #2) ─────────────
  // Optional structured signals. All default to `prefer_not_to_say` so
  // the new wizard step is fully skippable; users who answer get a
  // matchable signal on their profile + the FaithSection surfaces it
  // to the matched / mutual-disclosure side. No matcher gate at MVP.
  /** Hajj completion / intent. */
  hajjStatus: HajjStatus.default('prefer_not_to_say'),
  /** Umrah completion / intent — frequent enough to deserve a
   *  separate axis from Hajj (some users have multiple Umrah but
   *  not yet Hajj). */
  umrahStatus: UmrahStatus.default('prefer_not_to_say'),
  /** Quranic memorization (hifz). */
  quranMemorization: QuranMemorization.default('prefer_not_to_say'),
  /** Mosque attendance frequency. Distinct from `prays` (private)
   *  and `quranEngagement` (reading) — this captures community
   *  participation. */
  mosqueAttendance: MosqueAttendance.default('prefer_not_to_say'),

  // ─── Voice + video intros (matrimonial-polish PR #5) ──────────────
  // Optional short recordings attached to the profile. Both null when
  // the user hasn't uploaded one. Display rules: blur-until-match
  // equivalent — non-matched viewers see a "Available after matching"
  // placeholder, matched viewers + the owner play the recording.
  //
  // FEATURE-FLAGGED at MVP: the schema fields exist and the routes
  // work, but the recorder + profile section are gated by the
  // `features.profileIntros` flag (default off) until S3 storage +
  // Bedrock/Rekognition moderation are live. Once flipped on, the
  // existing per-photo pipeline pattern + the moderation status
  // machine ship together.
  /** Voice intro. Null when no recording, else carries the metadata.
   *  Server is the source of truth for `moderationStatus` — the UI
   *  only renders the player when status is `approved`. */
  voiceIntro: z
    .object({
      /** S3 key relative to the photos-private bucket family (we
       *  reuse the same bucket pattern). */
      s3Key: z.string().trim().min(1).max(512),
      /** Recorded duration in ms. Server enforces 3000-30000. */
      durationMs: z.number().int().min(3000).max(30000),
      moderationStatus: ProfileIntroModerationStatus,
      /** Human-readable moderation reason on rejection. Null when
       *  status is `pending` or `approved`. */
      moderationReason: z.string().trim().max(200).nullable(),
      /** ISO timestamp of last successful upload. */
      uploadedAt: IsoDateTimeSchema,
    })
    .nullable()
    .default(null),
  /** Video intro. Same shape + lifecycle as voiceIntro. Storage is
   *  a separate S3 key but the same bucket family. Recorded as MP4 /
   *  WebM with portrait orientation (480p target); server validates
   *  the format + duration in the moderation step. */
  videoIntro: z
    .object({
      s3Key: z.string().trim().min(1).max(512),
      durationMs: z.number().int().min(3000).max(30000),
      moderationStatus: ProfileIntroModerationStatus,
      moderationReason: z.string().trim().max(200).nullable(),
      uploadedAt: IsoDateTimeSchema,
    })
    .nullable()
    .default(null),

  // ─── Marriage intent ────────────────────────────────────────────────
  marriageTimeframe: MarriageTimeframe,
  familyInvolvement: FamilyInvolvement,
  wantsChildren: WantsChildren,
  relocation: Relocation,
  /** Where the couple intends to live post-marriage. */
  livingArrangement: LivingArrangement,
  /** Polygamy stance. Halal in Islam under conditions; meaningful
   *  matching signal in both directions. */
  polygamyPreference: PolygamyPreference,

  // ─── About ──────────────────────────────────────────────────────────
  bio: z.string().trim().max(2000),
  interests: z.array(z.string().trim().min(1).max(30)).max(15),
  personalityTraits: z.array(z.string().trim().min(1).max(30)).max(10),
  /** Bio-prompt answers — see BIO_PROMPTS in the trait registry. Each
   *  entry pairs a registered prompt key with the user's freeform
   *  answer (≤120 chars). Max 5 entries per user. promptKey
   *  validation against the BIO_PROMPTS list runs at the API edge,
   *  not here, so this schema stays usable without importing the
   *  registry (avoids a circular import). */
  promptAnswers: z
    .array(
      z.object({
        promptKey: z.string().trim().min(1).max(80),
        answer: z.string().trim().min(1).max(120),
      }),
    )
    .max(5)
    .default([]),

  // ─── Wali contact (optional, never publicly displayed) ──────────────
  // Plan §Family / Wali Involvement. All wali_* fields are optional +
  // nullable. The badge + match notifications only fire when the wali
  // has clicked the confirm link in their invitation email (see
  // waliConfirmedAt). Until then we treat the wali contact as "pending"
  // — no third party hears from us based on an unverified address.
  waliName: z.string().trim().max(80).nullable().optional(),
  waliRelationship: z.string().trim().max(40).nullable().optional(),
  waliEmail: z.string().trim().email().max(254).nullable().optional(),
  waliPhoneE164: z.string().nullable().optional(),
  /** Audit Spec F5 — language the wali is most comfortable in.
   *  Stored as an `IsoLanguageSchema` value; nullable so users with a
   *  wali who speaks the same language as the user can skip it
   *  without ceremony. Used by the chat header on cross-pair
   *  matches to surface "Both walis speak Urdu" when overlap exists. */
  waliLanguage: IsoLanguageSchema.nullable().optional(),
  /** Timestamp the wali clicked [Confirm role]. Powers the wali badge
   *  in the public projection (see WaliInvolvedFlagSchema below). */
  waliConfirmedAt: IsoDateTimeSchema.nullable().optional(),

  // ─── Privacy toggles ────────────────────────────────────────────────
  // Keep this list deliberately short. Plan §Product Principles bias
  // toward "Privacy is the default" via coarse data + server-side gating,
  // NOT per-user opt-outs of engagement-driving surfaces (last-active,
  // distance hint, profile-view recording). User-toggleable hides on
  // those surfaces consistently tank match velocity, so they're not
  // offered.
  blurUntilMatch: z.boolean().default(false),
  paidApproachOnly: z.boolean().default(false),

  // ─── Financial readiness + family background (audit Spec C2/C3/C4) ──
  // All optional; default `prefer_not_to_say` so existing profiles
  // aren't disrupted. Display on someone else's profile is gated by
  // the mutual-disclosure rule in profile-service's public-profile
  // route — you only see a field on their profile if you've disclosed
  // the same field on yours.
  incomeBracket: IncomeBracket.default('prefer_not_to_say'),
  currentlySupportsFamily: z.boolean().nullable().default(null),
  financiallyIndependent: z.boolean().default(true),
  mahrExpectation: MahrExpectation.default('prefer_not_to_say'),
  familyPractice: FamilyPractice.default('prefer_not_to_say'),
  familyEducationLevel: FamilyEducationLevel.default('prefer_not_to_say'),
  familyResidence: FamilyResidence.default('prefer_not_to_say'),

  // ─── Marital-history depth (audit Spec J2 — divorced/widowed) ───────
  // All optional. UI surfaces these only when the user is divorced or
  // widowed AND has filled them in. Never-married users see no UI for
  // these fields. Default off / null so existing profiles aren't
  // disrupted.
  yearsSinceMaritalChange: z.number().int().min(0).max(80).nullable().default(null),
  childrenLivingSituation: ChildrenLivingSituation.nullable().default(null),
  seekingPace: SeekingPace.nullable().default(null),
  /** When true, surfaces "Open about my past chapter" badge on the
   *  profile + boosts same-marital-status candidates in match-service. */
  openAboutPast: z.boolean().default(false),

  // ─── Location (precise coords; UI never displays exact values) ──────
  home: GeoPointSchema,
  /** Reverse-geocoded city. Populated by the wizard from the device's
   *  geolocation (or country-centroid fallback when permission is
   *  denied). Nullable for legacy rows pre-dating the geocode flow —
   *  display falls back to country-only in that case. The city is
   *  the finest granularity any other user ever sees. */
  homeCity: z.string().min(1).max(120).nullable().default(null),

  // ─── System ─────────────────────────────────────────────────────────
  completionPct: z.number().int().min(0).max(100),
  updatedAt: IsoDateTimeSchema,
  materiallyChangedAt: IsoDateTimeSchema,
});
export type Profile = z.infer<typeof ProfileSchema>;

/**
 * Public projection of a profile. What another user sees in feed/profile-view.
 * Excludes wali contact, exact coords, internal flags, embeddings.
 *
 * Distance + last-active are computed per-viewer and added at the API layer.
 */
export const PublicProfileSchema = ProfileSchema.pick({
  userId: true,
  displayName: true,
  gender: true,
  heightCm: true,
  maritalStatus: true,
  hasChildren: true,
  childrenCount: true,
  nationalityIso: true,
  countryOfResidenceIso: true,
  homeCity: true,
  ethnicities: true,
  languages: true,
  educationLevel: true,
  educationDiscipline: true,
  profession: true,
  grewUpCountryIso: true,
  grewUpRegion: true,
  sect: true,
  subSect: true,
  religiosity: true,
  prays: true,
  fastsRamadan: true,
  quranEngagement: true,
  dressStyle: true,
  diet: true,
  alcohol: true,
  smoking: true,
  convertStatus: true,
  marriageTimeframe: true,
  familyInvolvement: true,
  wantsChildren: true,
  relocation: true,
  livingArrangement: true,
  polygamyPreference: true,
  bio: true,
  interests: true,
  personalityTraits: true,
  promptAnswers: true,
  paidApproachOnly: true,
  // Financial readiness + family background (audit Spec C2/C3/C4).
  // Each field is gated server-side by the mutual-disclosure rule —
  // appears in the response only when the viewer has disclosed the
  // same field on their own profile. Always present in the type so
  // the UI doesn't crash on a null — the field is just an enum value
  // (`prefer_not_to_say`) for non-disclosing viewers + non-disclosing
  // candidates alike.
  incomeBracket: true,
  currentlySupportsFamily: true,
  financiallyIndependent: true,
  mahrExpectation: true,
  familyPractice: true,
  familyEducationLevel: true,
  familyResidence: true,
  // Audit Spec J2 — divorced/widowed depth. Always present in the
  // type; surfaced on the profile UI only when the values are
  // populated AND the user opted in via `openAboutPast`.
  yearsSinceMaritalChange: true,
  childrenLivingSituation: true,
  seekingPace: true,
  openAboutPast: true,
}).extend({
  /** Audit Spec D4 — "What they're looking for" summary surfaced on
   *  the public profile. A deliberately narrow subset of preferences:
   *  enough for the viewer to assess fit at a glance, not enough to
   *  reveal sensitive matching weights or geo restrictions. The
   *  candidate's distance / countries-acceptable are intentionally
   *  excluded — leaking those would let viewers triangulate the
   *  candidate's location preferences (and indirectly their location).
   *  Always present; populated server-side from profile_preferences. */
  lookingFor: z.object({
    ageMin: z.number().int().min(18).max(120),
    ageMax: z.number().int().min(18).max(120),
    sectAcceptable: z.array(z.string()),
    maritalStatusAcceptable: z.array(z.string()),
    wantsChildrenRequired: z.array(z.string()),
    /** 1-5; mirrors the profile religiosity scale. */
    religiosityMin: z.number().int().min(1).max(5),
  }),
  /** DOB-derived age. Computed at projection time. */
  age: z.number().int().min(18).max(120),
  /** Per-viewer distance in km. Null if hidden or out of scope. */
  distanceKm: z.number().int().nullable(),
  /** Coarse last-active bucket. Always present for active accounts;
   *  paused/banned/deleted users surface no last-active signal. */
  lastActiveBucket: z.enum(['now', 'today', 'this_week', 'recently', 'old']).nullable(),
  /** Whether viewer is a match (drives photo blur/clear decision). */
  isMatch: z.boolean(),
  /** Whether this user has a CONFIRMED wali (waliConfirmedAt is set).
   *  Powers the "Wali involved" badge on the profile card. We never
   *  expose the wali contact itself in the public projection — only
   *  the boolean fact-of-confirmed-wali. */
  waliInvolved: z.boolean(),
  /** Whether the viewer has favorited this profile. Drives the
   *  filled-star state on the head-tile action row. Private — the
   *  owner of this profile never learns who favorited them. */
  isFavorited: z.boolean(),
  /** Approved photos in position order (1 = primary). Each entry
   *  carries its id (caller resolves the signed URL separately via
   *  GET /profiles/photos/:id/url) and whether the variant the
   *  resolver would serve is the blurred one given the viewer's
   *  match state + the owner's blur-until-match toggle. */
  photos: z.array(
    z.object({
      id: z.string(),
      position: z.number().int().min(1).max(3),
      isBlurred: z.boolean(),
    }),
  ),
});
export type PublicProfile = z.infer<typeof PublicProfileSchema>;

// ─── Wali edit schema (used by profile-service PATCH /me/profile) ───
//
// Carved out as its own schema so the route handler can validate the
// wali slice independently — wali edits go through a separate code
// path (rate limit, invitation email side-effects) from the rest of
// profile-edit fields.
//
// All fields are nullable; sending `null` for an existing field is the
// "remove" gesture. Sending an updated `waliEmail` triggers a fresh
// invitation to the new address.
export const WaliEditSchema = z.object({
  waliName: z.string().trim().max(80).nullable(),
  waliRelationship: z.string().trim().max(40).nullable(),
  waliEmail: z.string().trim().email().max(254).nullable(),
  waliPhoneE164: z.string().nullable(),
  // Audit Spec F5 — optional. Send `null` to clear.
  waliLanguage: IsoLanguageSchema.nullable().optional(),
});
export type WaliEdit = z.infer<typeof WaliEditSchema>;
